import { useState, useLayoutEffect, useRef } from 'react';

/**
 * Determines how many featured items fit in the available horizontal space.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - Ref to the featured nav bar container.
 * @param {number} itemCount - Total number of featured items.
 * @returns {number} visibleCount - Number of items that fit in the available space.
 */
const useFeaturedOverflow = (containerRef, itemCount) => {
	const [visibleCount, setVisibleCount] = useState(itemCount);
	const widthsRef = useRef([]);

	// useLayoutEffect (not useEffect) so measure() runs synchronously before the
	// browser paints: visibleCount is corrected to the real fit-count before the
	// user ever sees a frame, avoiding a first-paint flash of every item briefly
	// rendering before the ResizeObserver measurement collapses the overflow.
	useLayoutEffect(() => {
		const container = containerRef?.current;
		widthsRef.current = [];

		if (!container || itemCount === 0) {
			setVisibleCount(0);
			return undefined;
		}

		const measure = () => {
			// Refresh the cached width for every item currently mounted, and
			// fall back to the last cached value for anything trimmed off.
			// visibleCount starts equal to itemCount, so every item is mounted
			// on the first pass and gets a real width cached before any
			// trimming happens. Without this cache, a fit-check could only
			// ever measure whatever's *currently* rendered -- once an item is
			// trimmed off (its DOM node removed), there'd be no way to detect
			// that it fits again once the container grows back, so the count
			// could only ever ratchet down, never recover.
			const { children } = container;
			for (let i = 0; i < children.length; i += 1) {
				widthsRef.current[i] = children[i].offsetWidth;
			}

			const { offsetWidth: availableWidth } = container;
			let usedWidth = 0;
			let count = 0;

			for (let i = 0; i < itemCount; i += 1) {
				const childWidth = widthsRef.current[i];
				if (childWidth === undefined) break;
				if (usedWidth + childWidth > availableWidth) break;
				usedWidth += childWidth;
				count += 1;
			}

			setVisibleCount(count);
		};

		const observer = new ResizeObserver(measure);
		observer.observe(container);
		measure();

		return () => observer.disconnect();
	}, [containerRef, itemCount]);

	return visibleCount;
};

export default useFeaturedOverflow;
